<template>
  <div class="create-comment-donation">
    <div class="create-comment-donation__info-box">
      <span class="donation-form__donation">
        <svg class="icon">
          <use href="#hand-holding-heart" />
        </svg>
        {{ texts.singleComment.hasDonated }}
      </span>
      <p class="create-comment-donation__info-text">
        {{
          sprintf(
            texts.createComment.donationInfoQuery,
            orderInfo.possessiveName
          )
        }}
      </p>
    </div>

    <div v-if="!noDonationsAllowed && donationSettings" class="donation-form">
      <template v-if="!isDonating && !hasDonated">
        <div class="donation-form__inputs-container" :style="{ opacity: gettingAvailableFunds ? 0.3 : 1 }">
          <div v-if="!hasMemorialFund" class="donation-form__input-wrapper donation-form__select-wrapper">
            <h3 class="donation-form__gift-heading">
              {{ texts.createComment.donationGiftOrgSelectHeading }}
            </h3>
            <v-select v-model="selectedFund" :options="availableFundsOptions" :placeholder="fundSelectPlaceholder"
              :searchable="true" :clearable="true" :disabled="!availableFundsOptions.length" :class="{
                disabled: !availableFundsOptions.length,
                'has-errors': selectedFundHasErrors,
              }" />
          </div>
        </div>

        <template v-if="shouldShowDonationForm">
          <div class="donation-form__form-inputs-container">
            <div class="donation-form__form-inputs" :style="{ opacity: gettingAvailableFunds ? 0.3 : 1 }">
              <h3 class="donation-form__gift-heading">
                {{ texts.createComment.donationGiftSizeHeading }}
              </h3>
              <div class="donation-form__sum-btns-container">
                <div v-for="donationAmount in donationSettings.amountList" :key="donationAmount"
                  class="donation-form__sum-btn-wrapper">
                  <button class="donation-form__sum-btn" :class="{ selected: donationAmount === parseInt(amount) }"
                    @click.prevent="setAmount(donationAmount)">
                    {{ donationAmount }} kr
                  </button>
                </div>
                <div class="donation-form__input-wrapper flex">
                  <input v-model="amount" type="number" :placeholder="texts.createComment.amountPlaceholderText + '*'
                    " :class="amountHasErrors && 'has-errors'" />
                </div>
              </div>
              <div class="donation-form__amount-information">
                <p class="donation-form__fee-display-info" v-text="texts.createComment.donationDisplayInfo" />
                <transition name="reveal-larger-row">
                  <div v-show="amount > 0" class="donation-form__fee-display">
                    <span>{{ texts.createComment.totalLabel }}:
                      {{ totalAmount }} kr</span>
                  </div>
                </transition>
                <p v-text="texts.createComment.donationFeeDisclaimer" />
              </div>
              <div class="donation-form__input-wrapper">
                <input v-model="name" type="text" name="Name" :placeholder="texts.common.yourName + '*'"
                  :class="nameHasErrors && 'has-errors'" />
              </div>
              <div class="donation-form__input-wrapper email">
                <input v-model="email" type="email" name="Email" :placeholder="texts.common.email + '*'"
                  :class="emailHasErrors && 'has-errors'" />
              </div>
              <div v-if="personalNumberFieldIsEnabled">
                <div class="donation-form__input-wrapper">
                  <input v-model="personalIdentityNumber" type="text" name="personal_identity_number"
                    :placeholder="personalIdentityNumberPlaceholder"
                    :class="{ 'has-errors': personalIdentityNumberHasErrors }" maxlength="13"
                    @blur="formatPersonalIdentityNumber" />
                </div>

                <div class="donation-form__tax-reduction-information">
                  <p v-html="texts.createComment.taxReductionRequirementInformation
                    " />
                </div>
              </div>
              <div v-if="paymentProviderRequiresPhoneNumber" class="donation-form__input-wrapper phone">
                <input v-model="phoneNumber" type="tel" name="Phone" :class="phoneHasErrors && 'has-errors'"
                  :placeholder="texts.common.phoneNumber + '*'" :required="true" />
              </div>

              <p v-if="showErrors && formErrors" class="form-errors" v-text="formErrors" />
            </div>
          </div>

          <div class="obituary-comments__submit-container">
            <button class="btn btn-cta donation-form__submit-button" :class="{
              disabled: disabled,
              'donation-form__submit-button__swish': paymentIsSwish,
            }" @click.prevent="disabled ? triggerShowErrors() : donate()">
              <span v-text="donateButtonText" />
              <img v-if="paymentIsSwish" src="/wp-content/assets/icons/swish-logo.svg" alt="Swish logotyp" />
            </button>
          </div>
          <template v-if="paymentIsSwish">
            <div class="donation-form__information">
              <p class="donation-form__payment-method-switch">
                {{ texts.createComment.payWithCardText }}
                <button class="btn link" @click.prevent="setPaymentProviderToCard">
                  {{ texts.createComment.clickHereButtonText }}
                </button>
              </p>
            </div>
          </template>
          <template v-if="paymentIsCard && swishCanBeSelected">
            <div class="donation-form__information">
              <p class="donation-form__payment-method-switch">
                {{ texts.createComment.payWithSwishText }}
                <button class="btn link" @click.prevent="setPaymentProviderToSwish">
                  {{ texts.createComment.clickHereButtonText }}
                </button>
              </p>
            </div>
          </template>
          <p v-html="`${texts.createComment.acceptTextDonation} ${texts.createComment.recipientFeeDescription}`
            " />
          <p class="donation-form__fee-info" v-text="texts.createComment.donationPaymentInfo" />
        </template>
        <create-comment-fund-information v-if="!!orderInfo.memorialFund" :memorial-fund="orderInfo.memorialFund"
          :display-image="false" />
      </template>
      <template v-if="isDonating && !hasDonated">
        <div class="donation-form__loading-container">
          <div v-if="!canCheckStatus" class="donation-form__loading-wrapper">
            <Loader :loading="true" />
          </div>
          <template v-if="canCheckStatus && paymentIsSwish">
            <SwishQRLoading v-if="swishQrCode" :svg-data="swishQrCode" />
            <div class="obituary-comments__submit-container">
              <button v-if="showManualCheckButton" class="btn btn-cta" :class="isCheckingStatus && 'disabled'"
                :disabled="isCheckingStatus" @click.prevent="manualCheckStatus()">
                {{ texts.createComment.checkDonationStatus }}
              </button>
            </div>
          </template>
          <template v-else-if="canCheckStatus && paymentIsCard">
            <iframe v-if="iframe" :src="iframe" frameborder="0" allowtransparency="true" allowfullscreen="true"
              scrolling="no" class="donation-form__iframe" />
            <div class="obituary-comments__submit-container">
              <button v-if="showManualCheckButton" class="btn btn-cta" :class="isCheckingStatus && 'disabled'"
                :disabled="isCheckingStatus" @click.prevent="manualCheckStatus()">
                {{ texts.createComment.checkDonationStatus }}
              </button>
            </div>
          </template>
          <h3 v-text="donationInProcessInstruction" />
        </div>
      </template>
      <template v-if="hasDonated">
        <div class="donation-form__loading-container">
          <svg class="icon icon--3x">
            <use href="#circle-check" />
          </svg>
          <h3 v-text="texts.createComment.donationSuccess" />
          <p v-text="texts.createComment.donationSuccessNextStep" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { formatSSN } from 'Library/stringFormatHelpers';
import { useIsMobile } from 'Main/composables/useIsMobile';
import { useCurrentSite } from 'Composables/useCurrentSite';
import Helpers from 'Modules/Helpers';
import { sprintf } from 'sprintf-js';
import vSelect from 'vue-select';
import { mapActions, mapMutations, mapState } from 'vuex';
import { validateEmail, validatePhoneNumber } from '../../helpers';
import { useCreateComment } from '../../composables/useCreateComment';
import Loader from '../Loader.vue';
import CreateCommentFundInformation from './CreateCommentFundInformation.vue';
import SwishQRLoading from './SwishQRLoading.vue';

const PAYMENT_PROVIDER_CARD = 'ping_card';
const PAYMENT_PROVIDER_SWISH = 'ping_swish_m';

const LOWER_LIMIT_FOR_ONE_TIME_DONATIONS = 1;

export default {
  name: 'CreateCommentDonation',

  components: {
    CreateCommentFundInformation,
    SwishQRLoading,
    Loader,
    vSelect,
  },

  setup() {
    const { isMobile, isTablet, isMobileDevice } = useIsMobile();
    const { currentSiteUpperCase, currentSiteId, isSwedishSite, isDanishSite } =
      useCurrentSite();
    const {
      files,
      hasFiles,
      resetFiles,
      resetInput,
      resetDonation,
      persist,
      toggleModal: createCommentToggleModal,
    } = useCreateComment();
    return {
      isMobile,
      isTablet,
      isMobileDevice,
      currentSiteUpperCase,
      currentSiteId,
      isSwedishSite,
      isDanishSite,
      files,
      hasFiles,
      resetFiles,
      resetInput,
      resetDonation,
      persist,
      createCommentToggleModal,
    };
  },

  props: {
    orderInfo: {
      type: Object,
      default: null,
    },
  },

  data() {
    let availableDonationTypes = [];

    if (
      lavendlaObituary.donationSettings &&
      lavendlaObituary.donationSettings.donationTypes.length > 0
    ) {
      availableDonationTypes = lavendlaObituary.donationSettings.donationTypes;
    }

    const swishCanBeSelected = availableDonationTypes.includes(
      PAYMENT_PROVIDER_SWISH
    );

    return {
      amount: '',
      phoneNumber: '',
      personalIdentityNumber: null,
      willDonate: false,
      showErrors: false,
      errorMessage: null,
      texts: lavendlaObituary,
      donationSettings: lavendlaObituary.donationSettings,
      swishQrCode: null,
      showManualCheckButton: false,
      hasGoneToNextStep: false,
      selectedFund: null,
      paymentProvider: null,
      iframe: null,
      swishCanBeSelected,
      currentSite: theme_vars.currentSite,
    };
  },

  computed: {
    ...mapState('createComment', ['comment']),
    ...mapState('comments', ['commentsList']),
    ...mapState('donations', [
      'isDonating',
      'donation',
      'hasDonated',
      'isCheckingStatus',
      'canCheckStatus',
      'pennyBridgeId',
      'description',
      'noDonationsAllowed',
      'availableFunds',
      'gettingAvailableFunds',
    ]),

    hasMemorialFund() {
      return !!this.orderInfo?.memorialFund;
    },

    shouldShowDonationForm() {
      return Boolean(this.hasMemorialFund || this.selectedFund);
    },

    name: {
      get() {
        return this.$store.state.createComment.name;
      },
      set(value) {
        this.$store.commit('createComment/updateName', value);
      },
    },

    email: {
      get() {
        return this.$store.state.createComment.email;
      },
      set(value) {
        this.$store.commit('createComment/updateEmail', value);
      },
    },

    paymentIsSwish() {
      return this.paymentProvider === PAYMENT_PROVIDER_SWISH;
    },

    paymentIsCard() {
      return this.paymentProvider === PAYMENT_PROVIDER_CARD;
    },

    paymentProviderRequiresPhoneNumber() {
      return this.paymentIsSwish;
    },

    disabled() {
      if (!this.hasMemorialFund && !this.availableFundsOptions.length) {
        return true;
      }

      if (!this.hasValidAmount) {
        return true;
      }

      if (!this.hasMemorialFund && !this.selectedFund) {
        return true;
      }

      if (!this.name) {
        return true;
      }

      if (!validateEmail(this.email)) {
        return true;
      }

      if (
        this.paymentProviderRequiresPhoneNumber &&
        !validatePhoneNumber(this.phoneNumber)
      ) {
        return true;
      }

      return !this.personalIdentityNumberHasValidLength();
    },

    formErrors() {
      if (!this.disabled && !this.errorMessage) {
        return null;
      }

      if (
        this.name === '' &&
        !this.hasValidAmount &&
        !validatePhoneNumber(this.phoneNumber) &&
        !validateEmail(this.email) &&
        !this.hasMemorialFund &&
        !this.selectedFund
      ) {
        return this.texts.createComment.formValidationRequiredFields;
      }

      if (this.name === '') {
        return this.texts.createComment.formValidationNameRequired;
      }

      if (!validateEmail(this.email)) {
        return this.texts.createComment.formValidationEmailRequired;
      }

      if (!this.hasValidAmount) {
        return this.texts.createComment.formValidationDonationAmountRequired;
      }

      if (!this.hasMemorialFund && !this.selectedFund) {
        return this.texts.createComment.formValidationFundRequired;
      }

      if (
        this.paymentProviderRequiresPhoneNumber &&
        !validatePhoneNumber(this.phoneNumber)
      ) {
        return this.texts.createComment.formValidationPhoneRequired;
      }

      if (!this.personalIdentityNumberHasValidLength()) {
        return this.texts.createComment
          .formValidationPersonalIdentityNumberLength;
      }

      if (this.errorMessage) {
        return this.errorMessage;
      }

      return this.texts.createComment.formValidationRequiredFields;
    },

    availableFundsOptions() {
      if (!this.availableFunds) {
        return [];
      }

      return this.availableFunds.map(({ label, value }) => {
        return {
          label,
          value,
        };
      });
    },

    selectedFundData() {
      if (this.selectedFund) {
        return this.availableFunds.find(
          (fund) => fund.value === this.selectedFund.value
        );
      }

      if (this.orderInfo?.memorialFund) {
        return {
          label: this.orderInfo.memorialFund.name,
          value: this.orderInfo.memorialFund.term_id,
          pennybridge_id: this.orderInfo.memorialFund.pennybridge_id,
          api_supplier_id: this.orderInfo.memorialFund.api_supplier_id,
        };
      }

      return [];
    },

    nameHasErrors() {
      return this.showErrors && this.name === '';
    },

    phoneHasErrors() {
      return this.showErrors && !validatePhoneNumber(this.phoneNumber);
    },

    emailHasErrors() {
      return this.showErrors && !validateEmail(this.email);
    },

    amountHasErrors() {
      return this.showErrors && !this.hasValidAmount;
    },

    selectedFundHasErrors() {
      return this.showErrors && !this.selectedFund;
    },

    personalIdentityNumberHasErrors() {
      if (!this.showErrors) {
        return false;
      }

      return !this.personalIdentityNumberHasValidLength();
    },

    personalIdentityNumberPlaceholder() {
      return `${this.texts.common.personalIdentityNumber} (${this.texts.common.optional})`;
    },

    donateButtonText() {
      if (this.paymentIsSwish) {
        return this.texts.createComment.donateButtonSwish;
      }

      return this.texts.createComment.donateButton;
    },

    donationInProcessInstruction() {
      if (this.paymentIsSwish) {
        return this.texts.createComment.donationHoldInstructionSwish;
      }

      return this.texts.createComment.donationHoldInstructionCard;
    },

    fundSelectPlaceholder() {
      return this.gettingAvailableFunds
        ? this.texts.createComment.fetchingMemorialFunds
        : this.texts.createComment.searchForAMemorialFund;
    },

    totalAmount() {
      return parseInt(this.donationSettings.fee) + this.parsedDonationAmount;
    },

    hasValidAmount() {
      return this.parsedDonationAmount >= LOWER_LIMIT_FOR_ONE_TIME_DONATIONS;
    },

    parsedDonationAmount() {
      if (this.amount && !isNaN(this.amount)) {
        const intParsedAmount = parseInt(this.amount);

        if (intParsedAmount > 0) {
          return intParsedAmount;
        }
      }

      return 0;
    },

    personalNumberFieldIsEnabled() {
      return this.isSwedishSite;
    },
  },

  watch: {
    selectedFund() {
      this.setPennyBridgeId(this.selectedFundData?.pennybridge_id);
      this.setDefaultPaymentProvider();
    },
  },

  created() {
    this.setHasDonated(false);

    if (
      !this.hasMemorialFund &&
      !this.availableFunds &&
      !this.gettingAvailableFunds
    ) {
      this.getAvailableFunds();
    }

    this.setDefaultPaymentProvider();
  },

  methods: {
    ...mapMutations('donations', [
      'setHasDonated',
      'setIsDonating',
      'setCanCheckStatus',
      'setPennyBridgeId',
    ]),
    ...mapActions('donations', [
      'addDonation',
      'checkDonationStatus',
      'getAvailableFunds',
    ]),
    ...mapActions('createComment', ['nextStep', 'updateSteps']),
    validateEmail,
    validatePhoneNumber,
    sprintf,

    setAmount(newAmount) {
      this.amount = newAmount;
    },

    setDefaultPaymentProvider() {
      this.paymentProvider = this.swishCanBeSelected
        ? PAYMENT_PROVIDER_SWISH
        : PAYMENT_PROVIDER_CARD;
    },

    donate() {
      if (!this.hasValidAmount) {
        return;
      }

      this.addDonation({
        amount: this.parsedDonationAmount,
        name: this.name,
        comment: this.comment,
        selectedFundName: this.selectedFund?.label,
        pennyBridgeId:
          this.pennyBridgeId ?? this.selectedFundData?.pennybridge_id,
        apiSupplierId: this.selectedFundData?.api_supplier_id,
        email: this.email,
        phoneNumber: this.phoneNumber,
        personalIdentityNumber: this.personalIdentityNumber,
        obituaryId: this.orderInfo.obituaryId,
        orderId: this.orderInfo.orderId,
        paymentProvider: this.paymentProvider,
      })
        .then((response) => {
          const { donationData } = response;
          if (!donationData) {
            return;
          }

          if (donationData.iframe) {
            this.iframe = donationData.redirectUrl;

            return;
          }

          this.swishQrCode = donationData.qrCode;

          if (this.isMobileDevice()) {
            window.location = donationData.redirectUrl;
          }
        })
        .then(() => this.setCanCheckStatus(true))
        .then(() => this.automaticStatusCheck())
        .catch((error) => {
          if (!error) {
            return;
          }

          this.errorMessage = error?.message || this.texts.createComment.unknownError;
          this.triggerShowErrors();

          Helpers.obituaryGreetingsLog(
            'DonationError',
            this.orderInfo.obituaryId,
            this.errorMessage,
          );
        });
    },

    manualCheckStatus() {
      this.checkDonation();
    },

    automaticStatusCheck() {
      let counter = 0;
      const maxTries = this.paymentIsSwish ? 50 : 360;
      const retryTime = this.paymentIsSwish ? 1500 : 5000;

      const interval = setInterval(() => {
        if (this.hasDonated) {
          clearInterval(interval);

          return;
        }

        this.checkDonation();
        counter++;
        if (counter === maxTries) {
          clearInterval(interval);
          this.showManualCheckButton = true;
        }

        if (this.hasDonated) {
          clearInterval(interval);
        }
      }, retryTime);
    },

    checkDonation() {
      this.checkDonationStatus()
        .catch((error) => {
          if (!error) {
            return;
          }

          this.errorMessage = error?.message || this.texts.createComment.unknownError;
          this.triggerShowErrors();
          this.setCanCheckStatus(false);
        })
        .then(() => {
          if (this.hasDonated && !this.hasGoneToNextStep) {
            this.hasGoneToNextStep = true;
            this.setCanCheckStatus(false);
            setTimeout(() => {
              Helpers.obituaryGreetingsLog(
                'Donation',
                this.orderInfo.obituaryId
              );
              Helpers.gtmObituaryEvent('donation');
              this.updateSteps('donation');
              this.nextStep();
            }, 2000);
          }
        });
    },

    triggerShowErrors() {
      this.showErrors = true;
    },

    personalIdentityNumberHasValidLength() {
      if (!this.personalIdentityNumber) {
        return true;
      }

      return this.personalIdentityNumber.length >= 10;
    },

    formatPersonalIdentityNumber() {
      if (!this.personalIdentityNumberHasValidLength()) {
        return this.personalIdentityNumber;
      }

      this.personalIdentityNumber = formatSSN(this.personalIdentityNumber);
    },

    setPaymentProviderToCard() {
      this.paymentProvider = PAYMENT_PROVIDER_CARD;
    },

    setPaymentProviderToSwish() {
      this.paymentProvider = PAYMENT_PROVIDER_SWISH;
    },
  },
};
</script>
